import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { find, identity } from 'lodash/fp';

import { stylingHelpers } from '../../designTokens';
import Spacing from '../layout/Spacing';
import Icon from '../chrome/Icon';
import Image from '../chrome/Image';
import Text from '../chrome/Text';

const { flexCenter } = stylingHelpers;

const containerBaseStyles = () => css`
  ${flexCenter()};
  line-height: 0.8;
`;

const Container = styled('div')(containerBaseStyles);

const textWrapperBaseStyles = () => css`
  display: flex;
  flex-wrap: wrap;
  text-align: center;
`;

const TextWrapper = styled('div')(textWrapperBaseStyles);

const textRowBaseStyles = ({ textOrder }) =>
  textOrder &&
  css`
    order: ${textOrder};
  `;

const TextRow = styled(Spacing)(textRowBaseStyles);

const iconContainerColorStyles = ({ theme, variant }) => css`
  color: ${theme.colors.heatStreak[variant]?.start || theme.colors.heatStreak.image.start};
`;

const IconContainer = styled('div')(iconContainerColorStyles);

const primaryTextBaseStyles = ({ theme, variant, size, textOrder }) => {
  const styleMap = {
    bit: {
      marginTop: textOrder === 1 ? '3px' : 0,
      lineHeight: '10px',
    },
    kilo: {
      marginTop: 0,
      lineHeight: textOrder === 1 ? '10px' : '13px',
    },
    peta: {
      marginTop: 0,
      lineHeight: '18px',
    },
    zetta: {
      marginTop: 0,
      lineHeight: '22px',
    },
  };

  const textStyles = styleMap[size] || styleMap.bit;

  return css`
    ${textStyles}
    color: ${theme.colors.heatStreak[variant]?.start || theme.colors.white};
  `;
};

const PrimaryText = styled(Text)(primaryTextBaseStyles);

const secondaryTextBaseStyles = ({ size, textOrder }) => {
  const styleMap = {
    bit: {
      marginTop: '-1px',
      lineHeight: '11px',
    },
    byte: {},
    kilo: {
      marginTop: '-3px',
      lineHeight: '11px',
    },
    mega: {},
    giga: {
      marginTop: textOrder === 2 ? 0 : '1px',
      lineHeight: '11px',
    },
    tera: {},
    peta: {
      marginTop: 0,
      lineHeight: '15px',
    },
    yotta: {
      marginTop: 0,
      lineHeight: '20px',
    },
    zetta: {
      marginTop: 0,
      lineHeight: textOrder === 2 ? '17px' : '22px',
    },
  };

  const textStyles = styleMap[size] || styleMap.kilo;

  return css`
    ${textStyles}
  `;
};

const SecondaryText = styled(Text)(secondaryTextBaseStyles);

const getIconSize = ({ size }) => {
  const sizeMap = {
    bit: 'sm',
    byte: 'lg',
    kilo: '2x',
    mega: '3x',
  };

  return sizeMap[size] || sizeMap.kilo;
};

const getTextSpacing = ({ size }) => {
  const marginMap = {
    kilo: 'atom',
    mega: 'bit',
  };

  return marginMap[size] || marginMap.kilo;
};

const getTextSize = ({ size, hasSecondaryText }) => {
  const sizeMap = {
    bit: hasSecondaryText ? 'bit' : 'mega',
    giga: hasSecondaryText ? 'giga' : 'tera',
    byte: hasSecondaryText ? 'kilo' : 'zetta',
    kilo: hasSecondaryText ? 'peta' : 'yotta',
    mega: hasSecondaryText ? 'zetta' : 'yotta',
  };
  return sizeMap[size];
};

const getSecondaryTextSize = ({ size }) => {
  const sizeMap = {
    bit: 'giga',
    byte: 'peta',
    kilo: 'zetta',
    mega: 'yotta',
  };

  return sizeMap[size] || sizeMap.kilo;
};

const stringNotEmpty = (text) => !!text && text !== undefined;

const StyledIcon = ({ variant, assetSrc, size }) => {
  if (variant === 'image') {
    return <Image src={assetSrc} />;
  }

  const icons = [
    variant === 'cold' && <Icon icon='snowflake' />,
    variant === 'locked' && <Icon icon='lock' />,
    variant === 'hot' && <Image src='https://cdn.rbi.digital/hockey/icon_flame.svg' />,
  ];

  return find(identity, icons) || <Icon icon='burn' size={getIconSize({ size })} />;
};

const HeatStreakText = ({
  primaryTextOrder,
  secondaryTextOrder,
  showIcon,
  text,
  secondaryText,
  variant,
  assetSrc,
  size,
  isFrench,
}) => {
  const hasText = stringNotEmpty(text);
  const hasSecondaryText = stringNotEmpty(secondaryText);
  const showTextWrapper = hasText || hasSecondaryText;
  const iconVisible = (!hasSecondaryText && showIcon) || (!hasSecondaryText && showIcon);
  return (
    <Container>
      {iconVisible && (
        <IconContainer variant={variant}>
          <StyledIcon variant={variant} assetSrc={assetSrc} size={size} />
        </IconContainer>
      )}
      {showTextWrapper && (
        <TextWrapper style={{ flexDirection: isFrench ? 'column-reverse' : 'column' }}>
          {hasText && (
            <TextRow textOrder={primaryTextOrder} sizeLeft={iconVisible && getTextSpacing({ size })}>
              <PrimaryText
                style={{ color: 'white', fontWeight: 'bold' }}
                as='span'
                size={variant === 'hot' ? 'tera' : getTextSize({ size, hasSecondaryText })}
                textOrder={primaryTextOrder}
                variant={variant}
                noMargin
              >
                {text}
              </PrimaryText>
            </TextRow>
          )}
          {hasSecondaryText && (
            <TextRow textOrder={secondaryTextOrder} sizeLeft={iconVisible && getTextSpacing({ size })}>
              <SecondaryText
                as='span'
                size={getSecondaryTextSize({ size })}
                textOrder={secondaryTextOrder}
                bold
                noMargin
              >
                {secondaryText}
              </SecondaryText>
            </TextRow>
          )}
        </TextWrapper>
      )}
    </Container>
  );
};

HeatStreakText.propTypes = {
  primaryTextOrder: PropTypes.number,
  secondaryTextOrder: PropTypes.number,
  showIcon: PropTypes.bool,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  secondaryText: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.string,
  assetSrc: PropTypes.string,
  size: PropTypes.string,
  isFrench: PropTypes.bool,
};

StyledIcon.propTypes = HeatStreakText.propTypes;

HeatStreakText.defaultProps = {
  primaryTextOrder: 1,
  secondaryTextOrder: 2,
  showIcon: true,
};

export default HeatStreakText;
